/**
 * PricingBaselinesPanel — admin CRUD para el Motor de Valoración.
 *
 * Los overrides listados aquí reemplazan los rangos heurísticos por
 * stratum que se aplican por defecto cuando el usuario publica una
 * propiedad y el sistema muestra el "precio sugerido".
 *
 * Clave del override: (city, neighborhood, property_type, operation).
 */
import React, { useEffect, useState } from 'react';
import { DollarSign, Plus, Trash2, RefreshCw, Info } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';

const OPERATIONS = [
  { value: 'venta', label: 'Venta' },
  { value: 'arriendo', label: 'Arriendo' },
];

const emptyForm = {
  city: '',
  neighborhood: '',
  property_type: '',
  operation: 'venta',
  price_per_m2_min: '',
  price_per_m2_max: '',
  sample_size: '',
  note: '',
};

const fmt = (n) => {
  if (n == null || Number.isNaN(+n)) return '—';
  return Number(n).toLocaleString('es-CO');
};

export default function PricingBaselinesPanel() {
  const { toast } = useToast();
  const [overrides, setOverrides] = useState([]);
  const [heuristic, setHeuristic] = useState({ venta: [], arriendo: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [list, table] = await Promise.all([
        api.listPricingBaselines(),
        api.pricingHeuristicTable(),
      ]);
      setOverrides(list.items || []);
      setHeuristic(table || { venta: [], arriendo: [] });
    } catch (err) {
      toast({ title: 'Error cargando baselines', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.city.trim()) {
      toast({ title: 'Falta la ciudad', description: 'La ciudad es obligatoria.', variant: 'destructive' });
      return;
    }
    const mn = parseInt(form.price_per_m2_min, 10);
    const mx = parseInt(form.price_per_m2_max, 10);
    if (Number.isNaN(mn) || Number.isNaN(mx) || mn <= 0 || mx <= 0) {
      toast({ title: 'Rango inválido', description: 'Ingresa precios COP/m² válidos.', variant: 'destructive' });
      return;
    }
    if (mn > mx) {
      toast({ title: 'Rango invertido', description: 'El mínimo no puede ser mayor al máximo.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.upsertPricingBaseline({
        city: form.city.trim(),
        neighborhood: form.neighborhood.trim() || null,
        property_type: form.property_type.trim() || null,
        operation: form.operation,
        price_per_m2_min: mn,
        price_per_m2_max: mx,
        sample_size: form.sample_size ? parseInt(form.sample_size, 10) : null,
        note: form.note.trim() || null,
      });
      toast({ title: 'Baseline guardado', description: 'El motor de valoración ya aplica el nuevo rango.' });
      setForm(emptyForm);
      await load();
    } catch (err) {
      toast({ title: 'No pudimos guardar', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    const label = `${b.city}${b.neighborhood ? ' · ' + b.neighborhood : ''}${b.property_type ? ' · ' + b.property_type : ''} · ${b.operation}`;
    if (!window.confirm(`¿Eliminar baseline para ${label}?`)) return;
    try {
      await api.deletePricingBaseline({
        city: b.city,
        neighborhood: b.neighborhood,
        property_type: b.property_type,
        operation: b.operation,
      });
      toast({ title: 'Baseline eliminado' });
      await load();
    } catch (err) {
      toast({ title: 'No pudimos eliminar', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6" data-testid="pricing-baselines-panel">
      {/* Intro */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-habita-mint text-habita grid place-items-center shrink-0">
            <DollarSign className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-serif text-xl font-bold text-habita-dark">Baselines del Motor de Valoración</h2>
            <p className="text-sm text-gray-600 mt-1">
              Estos overrides definen el rango de precio sugerido (COP por m²) que se muestra cuando alguien publica un inmueble.
              La clave es <strong>ciudad + barrio + tipo + operación</strong>. Si no hay override para un sector, el sistema aplica la tabla heurística por estrato.
            </p>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="font-semibold text-habita-dark mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Crear / actualizar override
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Ciudad *</Label>
            <Input value={form.city} onChange={(e) => setField('city', e.target.value)} placeholder="Ej: Medellín" data-testid="pricing-baseline-city" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Barrio (opcional)</Label>
            <Input value={form.neighborhood} onChange={(e) => setField('neighborhood', e.target.value)} placeholder="Ej: El Poblado" data-testid="pricing-baseline-neighborhood" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Tipo de inmueble (opcional)</Label>
            <Input value={form.property_type} onChange={(e) => setField('property_type', e.target.value)} placeholder="Ej: apartamento" data-testid="pricing-baseline-type" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Operación *</Label>
            <select
              value={form.operation}
              onChange={(e) => setField('operation', e.target.value)}
              className="mt-1.5 h-10 w-full rounded-md border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-habita"
              data-testid="pricing-baseline-operation"
            >
              {OPERATIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Precio min · COP/m² *</Label>
            <Input type="number" value={form.price_per_m2_min} onChange={(e) => setField('price_per_m2_min', e.target.value)} placeholder="Ej: 6500000" data-testid="pricing-baseline-min" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Precio max · COP/m² *</Label>
            <Input type="number" value={form.price_per_m2_max} onChange={(e) => setField('price_per_m2_max', e.target.value)} placeholder="Ej: 9000000" data-testid="pricing-baseline-max" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Tamaño muestra (opcional)</Label>
            <Input type="number" value={form.sample_size} onChange={(e) => setField('sample_size', e.target.value)} placeholder="Ej: 45" data-testid="pricing-baseline-sample" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Nota interna (opcional)</Label>
            <Input value={form.note} onChange={(e) => setField('note', e.target.value)} placeholder="Ej: Cierre Fedelonjas Q1 2026" data-testid="pricing-baseline-note" className="mt-1.5" />
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <Button onClick={submit} disabled={saving} className="btn-habita rounded-full h-11 px-6" data-testid="pricing-baseline-save-btn">
            {saving ? 'Guardando...' : 'Guardar baseline'}
          </Button>
          <Button variant="outline" onClick={load} className="rounded-full h-11 px-5">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Recargar
          </Button>
        </div>
      </div>

      {/* Lista de overrides */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="font-semibold text-habita-dark mb-4">Overrides activos ({overrides.length})</h3>
        {loading ? (
          <p className="text-sm text-gray-500 py-6 text-center">Cargando...</p>
        ) : overrides.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">Aún no hay overrides. El motor usa la tabla heurística por estrato.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]" data-testid="pricing-baselines-table">
              <thead className="bg-habita-cream text-left">
                <tr>
                  <th className="p-2.5 font-semibold text-habita-dark">Ciudad</th>
                  <th className="p-2.5 font-semibold text-habita-dark">Barrio</th>
                  <th className="p-2.5 font-semibold text-habita-dark">Tipo</th>
                  <th className="p-2.5 font-semibold text-habita-dark">Operación</th>
                  <th className="p-2.5 font-semibold text-habita-dark text-right">Min COP/m²</th>
                  <th className="p-2.5 font-semibold text-habita-dark text-right">Max COP/m²</th>
                  <th className="p-2.5 font-semibold text-habita-dark text-right">Muestra</th>
                  <th className="p-2.5 font-semibold text-habita-dark">Nota</th>
                  <th className="p-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {overrides.map((b, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="p-2.5 font-medium text-habita-dark">{b.city}</td>
                    <td className="p-2.5 text-gray-700">{b.neighborhood || '—'}</td>
                    <td className="p-2.5 text-gray-700">{b.property_type || '—'}</td>
                    <td className="p-2.5 text-gray-700 capitalize">{b.operation}</td>
                    <td className="p-2.5 text-right font-semibold text-habita">{fmt(b.price_per_m2_min)}</td>
                    <td className="p-2.5 text-right font-semibold text-habita">{fmt(b.price_per_m2_max)}</td>
                    <td className="p-2.5 text-right text-gray-600">{b.sample_size ?? '—'}</td>
                    <td className="p-2.5 text-gray-500 text-xs italic">{b.note || ''}</td>
                    <td className="p-2.5 text-right">
                      <button onClick={() => remove(b)} className="text-rose-600 hover:bg-rose-50 rounded-full p-1.5" data-testid={`pricing-baseline-delete-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Heurística por estrato (referencia) */}
      <div className="bg-habita-cream/60 rounded-2xl border border-gray-100 p-5">
        <h3 className="font-semibold text-habita-dark mb-3 flex items-center gap-2">
          <Info className="w-4 h-4" /> Heurística por estrato (referencia)
        </h3>
        <p className="text-xs text-gray-600 mb-4">
          Se aplican cuando no hay override específico para el sector. Rangos conservadores basados en referencias públicas.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {['venta', 'arriendo'].map((op) => (
            <div key={op} className="bg-white rounded-xl p-3 border border-gray-100">
              <p className="font-semibold text-habita-dark capitalize">{op}</p>
              <div className="mt-2 space-y-1 text-xs">
                {(heuristic[op] || []).map((s) => (
                  <div key={s.stratum} className="flex justify-between text-gray-600">
                    <span>Estrato {s.stratum}</span>
                    <span className="font-semibold text-habita">
                      {fmt(s.price_per_m2_min)} · {fmt(s.price_per_m2_max)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
